/**
 * When an item landed, in the fewest characters that still answer "is this new?".
 *
 * The feed is read like a transcript while a dig is running, so the question
 * being asked of a timestamp is almost always "did this arrive since I last
 * looked?" — which a clock time answers and a date does not. The date only
 * earns its place once the item is not from today.
 *
 * Pure, and in its own module: CRA's jest cannot resolve the component tree
 * these are used from, so the logic is tested here rather than through a
 * render.
 */

/** The stamp shown on the card. "" when there is no timestamp at all. */
export function feedTime(iso: string | null | undefined, now: Date = new Date()): string {
  const d = parse(iso);
  if (!d) return '';
  const clock = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay(d, now)) return clock;
  const day = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${day} ${clock}`;
}

/** The full stamp, for the tooltip — seconds included, because two items a
 *  second apart read as simultaneous at minute resolution and the order they
 *  landed in is the thing being checked. */
export function feedTimeTitle(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? d.toLocaleString() : '';
}

/** Whether an item landed after the given moment — what "new since I looked"
 *  means, kept here so the feed and the strip cannot disagree about it. */
export function isSince(iso: string | null | undefined, since: Date | null): boolean {
  const d = parse(iso);
  return !!d && !!since && d.getTime() > since.getTime();
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
